import { RefObject, useCallback, useMemo } from 'react'

import {
  CONTAINER_TO_APP_MESSAGE_TYPES,
  getIsContainerBridgeAvailable,
  postMessage,
  useAppListeners
} from '@inno-tools/container-bridge'

export const useContainerBridge = (
  iframeRef: RefObject<HTMLIFrameElement> | HTMLIFrameElement | null,
  { onNavigation }: { onNavigation?: (path: string) => void } = {}
) => {
  const getIframe = useCallback(
    () => (iframeRef && 'current' in iframeRef ? iframeRef.current : iframeRef),
    [iframeRef]
  )

  const getOrigin = useCallback(() => {
    const iframe = getIframe()
    return iframe ? new URL(iframe.src).origin : '*'
  }, [getIframe])

  const { historyAvailability } = useAppListeners({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    windowId: (getIframe()?.contentWindow as any)?.['__CONTAINER__']?.windowId,
    onNavigation,
    appOrigin: getOrigin()
  })

  const isContainerBridgeAvailable = getIsContainerBridgeAvailable(
    getIframe()?.contentWindow
  )

  const postBackMessage = useCallback(
    (windowId: string) => {
      const iframe = getIframe()
      if (!iframe?.contentWindow) return

      postMessage(
        iframe.contentWindow,
        { type: CONTAINER_TO_APP_MESSAGE_TYPES.BACK, windowId },
        getOrigin()
      )
    },
    [getIframe, getOrigin]
  )

  const postNavigateMessage = useCallback(
    (path: string, windowId: string) => {
      const iframe = getIframe()
      if (!iframe?.contentWindow) return

      postMessage(
        iframe.contentWindow,
        {
          type: CONTAINER_TO_APP_MESSAGE_TYPES.NAVIGATE,
          data: { path },
          windowId
        },
        getOrigin()
      )
    },
    [getIframe, getOrigin]
  )

  const postForwardMessage = useCallback(
    (windowId: string) => {
      const iframe = getIframe()
      if (!iframe?.contentWindow) return

      postMessage(
        iframe.contentWindow,
        { type: CONTAINER_TO_APP_MESSAGE_TYPES.FORWARD, windowId },
        getOrigin()
      )
    },
    [getIframe, getOrigin]
  )

  const messages = useMemo(
    () => ({
      postBackMessage,
      postForwardMessage,
      postNavigateMessage
    }),
    [postBackMessage, postForwardMessage, postNavigateMessage]
  )

  return {
    historyAvailability,
    isContainerBridgeAvailable,
    messages
  }
}
